export type PickemBracketFormat =
  | 'single_elimination'
  | 'double_elimination'
  | 'double_elimination_seeded';
export interface PickemBracketMatch {
  id: string;
  section: string;
  roundNumber: number;
  matchIndex: number;
  competitorTopId?: string | null;
  competitorBottomId?: string | null;
  winnerId?: string | null;
  sourceTopMatchId?: string | null;
  sourceBottomMatchId?: string | null;
}
export interface LineupSlot {
  bracketMatchId: string;
  slot: 'top' | 'bottom';
  competitorId: string;
}
export function startingMatches<T extends PickemBracketMatch>(
  matches: T[],
  format?: string,
): T[] {
  return matches
    .filter(
      (m) =>
        m.roundNumber === 1 &&
        (m.section === 'main' ||
          m.section === 'upper' ||
          (format === 'double_elimination_seeded' && m.section === 'lower')),
    )
    .sort(
      (a, b) =>
        a.section.localeCompare(b.section) || a.matchIndex - b.matchIndex,
    );
}
export function personalBracket<T extends PickemBracketMatch>(
  matches: T[],
  lineup: LineupSlot[],
  format?: string,
): T[] {
  const roots = new Set(startingMatches(matches, format).map((m) => m.id));
  const slots = new Map(
    lineup.map((s) => [s.bracketMatchId + ':' + s.slot, s.competitorId]),
  );
  return matches.map(
    (m): T => ({
      ...m,
      winnerId: null,
      competitorTopId: roots.has(m.id)
        ? (slots.get(m.id + ':top') ?? null)
        : null,
      competitorBottomId: roots.has(m.id)
        ? (slots.get(m.id + ':bottom') ?? null)
        : null,
    }),
  );
}
export function validateLineup(
  matches: PickemBracketMatch[],
  lineup: LineupSlot[],
  competitorIds: Set<string>,
  format?: string,
): string | null {
  const roots = new Set(startingMatches(matches, format).map((m) => m.id));
  if (
    lineup.some(
      (s) => !roots.has(s.bracketMatchId) || !competitorIds.has(s.competitorId),
    )
  )
    return 'Invalid starting slot or competitor';
  if (new Set(lineup.map((s) => s.competitorId)).size !== lineup.length)
    return 'Each entrant may start only once';
  if (
    new Set(lineup.map((s) => s.bracketMatchId + ':' + s.slot)).size !==
    lineup.length
  )
    return 'Duplicate starting slot';
  return null;
}
export function prunePicks(
  matches: PickemBracketMatch[],
  picks: Record<string, string>,
  format?: PickemBracketFormat,
) {
  const result = { ...picks };
  for (let i = 0; i <= matches.length; i++) {
    const slots = computeEffectiveCompetitors(matches, result, format);
    let changed = false;
    for (const [id, pick] of Object.entries(result)) {
      const s = slots.get(id);
      if (
        !s?.topId ||
        !s.bottomId ||
        (pick !== s.topId && pick !== s.bottomId)
      ) {
        delete result[id];
        changed = true;
      }
    }
    if (!changed) break;
  }
  return result;
}
export interface EffectiveSlots {
  topId?: string;
  bottomId?: string;
}

/**
 * Compute the "effective" top/bottom competitor for every match in a bracket,
 * given the actual match state (placed competitors + confirmed winners) and
 * the current user's pending picks. User picks are treated as hypothetical
 * winners that propagate forward through the bracket graph so the user can
 * predict the full photo finish — winners advance in the upper bracket,
 * losers drop into the lower bracket (double elimination).
 *
 * The bracket schema stores feeder edges in `sourceTopMatchId` /
 * `sourceBottomMatchId` (either a real match id or a `pending:section:round:index`
 * placeholder). Loser-drop destinations for double elimination are NOT stored
 * on the records, so this helper reconstructs them from the standard convention:
 *   - Upper R1 match i loser → Lower R1 match floor(i/2), slot i%2===0 ? top : bottom
 *   - Upper Rk (k≥2) match i loser → Lower R(2k-2), bottom slot, with the match
 *     index reversed within that round so the loser drops to the side fed by the
 *     other half of the upper bracket (avoids an immediate rematch).
 */
export function computeEffectiveCompetitors(
  matches: PickemBracketMatch[],
  userPicks: Record<string, string>,
  bracketFormat?: PickemBracketFormat,
): Map<string, EffectiveSlots> {
  const byId = new Map(matches.map((m) => [m.id, m]));
  const byRef = new Map<string, PickemBracketMatch>();
  for (const m of matches) {
    byRef.set(`pending:${m.section}:${m.roundNumber}:${m.matchIndex}`, m);
  }

  const slots = new Map<string, EffectiveSlots>();
  for (const m of matches) {
    slots.set(m.id, {
      topId: m.competitorTopId ?? undefined,
      bottomId: m.competitorBottomId ?? undefined,
    });
  }

  const getWinner = (matchId: string): string | undefined => {
    const m = byId.get(matchId);
    if (!m) return undefined;
    if (m.winnerId) return m.winnerId;
    return userPicks[matchId];
  };

  const getLoser = (matchId: string): string | undefined => {
    const s = slots.get(matchId);
    const winner = getWinner(matchId);
    if (!s || !winner) return undefined;
    if (s.topId && s.topId !== winner) return s.topId;
    if (s.bottomId && s.bottomId !== winner) return s.bottomId;
    return undefined;
  };

  const resolveSource = (
    source: string | null | undefined,
  ): string | undefined => {
    if (!source) return undefined;
    const m = byId.get(source) ?? byRef.get(source);
    if (!m) return undefined;
    return getWinner(m.id);
  };

  const loserDrops: Array<{
    upperId: string;
    lowerId: string;
    slot: 'top' | 'bottom';
  }> = [];
  const upperMatches = matches.filter((m) => m.section === 'upper');
  const findLower = (round: number, idx: number) =>
    matches.find(
      (m) =>
        m.section === 'lower' &&
        m.roundNumber === round &&
        m.matchIndex === idx,
    );

  const lowerRoundNumbers = matches
    .filter((m) => m.section === 'lower')
    .map((m) => m.roundNumber);
  const lastLowerRound =
    lowerRoundNumbers.length > 0 ? Math.max(...lowerRoundNumbers) : 0;

  if (bracketFormat === 'double_elimination_seeded') {
    // Seeded top-8: the upper loser always drops into the TOP slot (the lower
    // progression feeds the bottom). Upper R1 (Winners Semi) losers cross into
    // the Losers Quarter-Final (lower R2); the Winners Final loser drops into
    // the Losers Final (deepest lower round).
    for (const upper of upperMatches) {
      const lower =
        upper.roundNumber === 1
          ? findLower(2, upper.matchIndex === 0 ? 1 : 0)
          : findLower(lastLowerRound, 0);
      if (lower) {
        loserDrops.push({ upperId: upper.id, lowerId: lower.id, slot: 'top' });
      }
    }
  } else {
    for (const upper of upperMatches) {
      if (upper.roundNumber === 1) {
        const lower = findLower(1, Math.floor(upper.matchIndex / 2));
        if (lower) {
          loserDrops.push({
            upperId: upper.id,
            lowerId: lower.id,
            slot: upper.matchIndex % 2 === 0 ? 'top' : 'bottom',
          });
        }
      } else {
        // Cross the drop: reverse the index within the target lower round so the
        // loser falls into the match fed by the OTHER half of the upper bracket,
        // avoiding an immediate rematch (e.g. for 2 matches 0↔1).
        const targetRound = 2 * upper.roundNumber - 2;
        const roundMatches = matches
          .filter((m) => m.section === 'lower' && m.roundNumber === targetRound)
          .sort((a, b) => a.matchIndex - b.matchIndex);
        const reversedIndex = roundMatches.length - 1 - upper.matchIndex;
        const lower = roundMatches[reversedIndex];
        if (lower) {
          loserDrops.push({
            upperId: upper.id,
            lowerId: lower.id,
            slot: 'bottom',
          });
        }
      }
    }
  }

  for (let iteration = 0; iteration < 30; iteration++) {
    let changed = false;

    for (const match of matches) {
      const s = slots.get(match.id)!;

      if (!s.topId && match.sourceTopMatchId) {
        const resolved = resolveSource(match.sourceTopMatchId);
        if (resolved) {
          s.topId = resolved;
          changed = true;
        }
      }
      if (!s.bottomId && match.sourceBottomMatchId) {
        const resolved = resolveSource(match.sourceBottomMatchId);
        if (resolved) {
          s.bottomId = resolved;
          changed = true;
        }
      }
    }

    for (const drop of loserDrops) {
      const loser = getLoser(drop.upperId);
      if (!loser) continue;
      const target = slots.get(drop.lowerId);
      if (!target) continue;
      if (drop.slot === 'top' && !target.topId) {
        target.topId = loser;
        changed = true;
      } else if (drop.slot === 'bottom' && !target.bottomId) {
        target.bottomId = loser;
        changed = true;
      }
    }

    if (!changed) break;
  }

  return slots;
}
